// TxDOT look and feel for Cloudscape, from @txdot/theme (vendored in apps/web/vendor/, installed
// with `mise run theme:install --dir apps/web`; see https://gitlab.aws.dev/awsmikek/txdot-theme).
//
// Cloudscape keeps its own components and layout. This only:
//   - loads IBM Plex Sans (self-hosted, so the CSP's font-src 'self' still holds) and the --tx-*
//     design tokens from www.txdot.gov, and
//   - points Cloudscape's design tokens at them: TxDOT brand blue for primary/normal buttons, links,
//     focus rings, checked controls and selected items, TxDOT corner radius, and the TxDOT font.
// Values reference the CSS variables, so tokens.css stays the single source of truth.
//
// Dark mode (src/lib/colorMode.ts): @txdot/theme ships no Cloudscape overrides and no dark Cloudscape values (its
// only dark palette is the `.dark` class for its Tailwind/shadcn layer), so the dark values are set here, taken
// from that same palette: brand-30 (txdot.gov's --primary-color-dark) for actions, links and focus, brand-20 on
// hover, deep brand navies for hover and selected backgrounds. brand-50 is about 2:1 on Cloudscape's dark
// surfaces; brand-30 is at least 7:1 on them, and dark primary-button text is at least 7:1 on brand-30.
import { applyTheme, type Theme } from '@cloudscape-design/components/theming';
import '@txdot/theme/fonts.css';
import '@txdot/theme/tokens.css';

const tx = (name: string) => `var(--tx-${name})`;
const mode = (light: string, dark: string) => ({ light: tx(light), dark: tx(dark) });

export const txdotCloudscapeTheme: Theme = {
  tokens: {
    fontFamilyBase: tx('font-family'),
    fontFamilyHeading: tx('font-family'),

    colorBackgroundButtonPrimaryDefault: mode('brand-50', 'brand-30'),
    colorBackgroundButtonPrimaryHover: mode('brand-60', 'brand-20'),
    colorBackgroundButtonPrimaryActive: mode('brand-70', 'brand-30'),
    colorBorderButtonNormalDefault: mode('brand-50', 'brand-30'),
    colorTextButtonNormalDefault: mode('brand-50', 'brand-30'),
    colorBorderButtonNormalHover: mode('brand-70', 'brand-20'),
    colorTextButtonNormalHover: mode('brand-70', 'brand-20'),
    colorBackgroundButtonNormalHover: mode('brand-5', 'brand-90'),

    colorTextLinkDefault: mode('brand-50', 'brand-30'),
    colorTextLinkHover: mode('brand-70', 'brand-20'),
    colorTextAccent: mode('brand-50', 'brand-30'),
    colorBorderItemFocused: mode('focus-ring', 'brand-30'),
    colorBorderInputFocused: mode('brand-50', 'brand-30'),
    colorBackgroundControlChecked: mode('brand-50', 'brand-30'),
    colorBackgroundItemSelected: mode('brand-5', 'brand-100'),
    colorBorderItemSelected: mode('brand-50', 'brand-30'),

    borderRadiusButton: tx('radius-base'),
    borderRadiusInput: tx('radius-base'),
    borderRadiusContainer: tx('radius-md'),
  },
};

/** Apply the TxDOT overrides to every Cloudscape component on the page. Call once at startup. */
export function applyTxdotTheme(): void {
  applyTheme({ theme: txdotCloudscapeTheme });
}
